import { WifiOff } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { CALL_END_REASON_LABELS, type CallEndReason, type TwilioCallState } from "@/context/twilio.context";
import type { CallableLead } from "@/context/call.lead.popup.context";
import { getInitials } from "@/utils/strings";
import { cn } from "@/lib/utils";
import { formatDuration } from "./constants";

type Props = {
  lead: CallableLead;
  state: TwilioCallState;
  isOnHold: boolean;
  callStartedAt: Date | null;
  now: number;
  callEndReason: CallEndReason | null;
  errorMessage: string | null;
  isNetworkError: boolean;
};

const CallIdentity = ({
  lead,
  state,
  isOnHold,
  callStartedAt,
  now,
  callEndReason,
  errorMessage,
  isNetworkError,
}: Props) => (
  <div className="relative px-5 pt-2 pb-6 flex flex-col items-center text-center">
    {/* Avatar with pulse ring while ringing */}
    <div className="relative mb-4">
      {(state === "connecting" || state === "ringing") && (
        <span className="absolute inset-0 rounded-2xl bg-emerald-500/30 animate-ping" />
      )}
      <Avatar className="relative h-20 w-20 rounded-2xl shadow-lg">
        <AvatarFallback className="rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white text-2xl font-semibold">
          {getInitials(lead.name)}
        </AvatarFallback>
      </Avatar>
    </div>

    <h2 className="text-xl font-semibold text-white">{lead.name}</h2>

    {(lead.role || lead.company) && (
      <p className="text-sm text-white/60 mt-1">
        {[lead.role, lead.company].filter(Boolean).join(" · ")}
      </p>
    )}

    <p className="text-sm text-white/40 mt-1 font-mono tracking-wide">
      {lead.phoneNumber}
    </p>

    {/* Status line — duration when connected, "Calling…" while ringing,
        ended/error copy in their respective states. */}
    <div className="mt-5 min-h-[2.5rem] flex flex-col items-center justify-center">
      {state === "in-call" && isOnHold ? (
        <span className="text-amber-300 text-sm font-medium">Lead is on hold</span>
      ) : state === "in-call" && callStartedAt ? (
        <span className="text-white font-mono text-3xl tabular-nums font-light">
          {formatDuration(now - callStartedAt.getTime())}
        </span>
      ) : state === "connecting" || state === "ringing" ? (
        <span className="text-white/60 text-sm">Calling…</span>
      ) : state === "ended" ? (
        <span
          className={cn(
            "text-sm",
            callEndReason === "rejected" || callEndReason === "no-answer"
              ? "text-amber-300"
              : "text-white/60",
          )}
        >
          {callEndReason ? CALL_END_REASON_LABELS[callEndReason] : "Call ended"}
        </span>
      ) : state === "error" ? (
        <span className={cn(
          "flex flex-col items-center gap-1.5 text-sm max-w-xs",
          isNetworkError ? "text-orange-300" : "text-red-300",
        )}>
          {isNetworkError && <WifiOff className="w-5 h-5" />}
          {errorMessage ?? "Call failed"}
        </span>
      ) : (
        <span className="text-white/40 text-sm">Connecting your call…</span>
      )}
    </div>
  </div>
);

export default CallIdentity;
